import { useRouter } from '@abyss/web/hooks/useRouter';
import { Button, V2Button } from '@abyss/web/ui/Button';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { Text } from '@abyss/web/ui/Text';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { find } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../../common/AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../../../common/AdobeTagging/adobeModalTrackEvent';
import { adobeStandardSearchEvent } from '../../../common/AdobeTagging/adobeStandardSearchEvent';
import { Constants } from '../../../common/Constants';
import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import {
  getGeoLocationFromStorage,
  setGeoLocationToStorage,
} from '../../../common/PSXHeader/SearchBar/utils';
import { useAdobePageTrackEvent } from '../../../hooks/adobeHook/useAdobePageTrackEvent';
import { useGetFipsCode } from '../../../hooks/useGetFipsCode';
import { useGetPlanDefinitions } from '../../../hooks/useGetPlanDefinitions/useGetPlanDefinitions';
import { useGetPostalPoint } from '../../../hooks/useGetPostalPoint/useGetPostalPoint';
import { useLagoon } from '../../../hooks/useLagoon';
import { useLocation } from '../../../hooks/useLocation';
import { useGuestAcquisitionStore } from '../../../store/useGuestAcquisitionStore';
import { GuestAcquisitionState } from '../../../store/useGuestAcquisitionStore/guestAcquisitionStore';
import { useGuestStore } from '../../../store/useGuestStore';
import { GuestState } from '../../../store/useGuestStore/guestStore';
import { DateTime } from '../../../utils/DateTime';
import {
  buildPortalData,
  filterAvailablePlansByPlanName,
  getAvailablePlans,
  getGuestLocationPageName,
  getLobForPortalData,
  getLocationRequirement,
  setGuestPlanSelectionData,
  shouldSkipPlanSelection,
} from '../Guest.utils';
import { findAcquisitionByClientCode } from '../GuestAcquisition.utils';
import { getBlueGreenGQLApiUrl, isLawwGuest } from '../utils';
import { GeospatialModal } from './GeospatialModal';
import { LocationModal, MessagingText } from './GuestLocation.style';
import {
  GuestLocation,
  getShowDualCompletePlanMessaging,
  isSinglePlan,
} from './GuestLocation.utils';
import { PostalLocationData } from './GuestLocationInput';
import { GuestLocationSearch } from './GuestLocationSearch';
import {
  LocationSearchBoxWrapper,
  SelectedLocationWrapper,
} from './GuestLocationSelect.style';

type planDetails = {
  medicarePlanType: string;
  planIdentifier: string;
  planName: string;
  searchDirectory: string;
  years: [PlanYear];
  portalSource?: string;
};

type PlanYear = {
  planYear: string;
  reciprocityId: string;
  telehealthIndicator: boolean;
  displayAcceptsMedicaid: boolean;
  clusterKey: string;
  groupDriven: boolean;
  guestPath: string;
};

type GuestLocationSelectProps = {
  selectedLocation?: GuestLocation;
  setSelectedLocation: (a: GuestLocation) => void;
  isDataLoaded: boolean;
  setIsDataLoaded: (a: boolean) => void;
};

const getSelectLocationHeadingKey = (
  planName: string | undefined,
  lob: string,
  coverageType: string
) => {
  if (planName) {
    return ConstantsLagoon.GUEST_PLAN_SELECTION
      .SELECT_LOCATION_SINGLE_PLAN_HEADING_DEEPLINK;
  }

  if (isSinglePlan(lob, coverageType)) {
    return ConstantsLagoon.GUEST_PLAN_SELECTION
      .SELECT_LOCATION_SINGLE_PLAN_HEADING;
  }

  return ConstantsLagoon.GUEST_PLAN_SELECTION.SELECT_LOCATION_HEADING;
};

export const GuestLocationSelect = ({
  selectedLocation,
  setSelectedLocation,
  isDataLoaded,
  setIsDataLoaded,
}: GuestLocationSelectProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const psxLocation = getGeoLocationFromStorage();
  const [location, setLocation] = useState<GuestLocation>(
    selectedLocation || ({} as GuestLocation)
  );
  const [currentInputValue, setCurrentInputValue] = useState('');
  const [selectedCounty, setSelectedCounty] = useState('');
  const [showCountyDropdown, setShowCountyDropdown] = useState(false);
  const [usPostalPointId, setUsPostalPointId] = useState('');
  const [isGeospatialOpen, setIsGeospatialOpen] = useState(false);
  const [
    requireNavigateAfterLocationSelect,
    setRequireNavigateAfterLocationSelect,
  ] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<
    GuestLocation | undefined
  >(undefined);

  const { guestPlanSelectorData } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestPlanSelectorData: state.guestPlanSelectorData,
    }))
  );

  const { isNavigatingMaestro, requestData } = useGuestAcquisitionStore(
    useShallow((state: GuestAcquisitionState) => ({
      isNavigatingMaestro: state.isNavigatingMaestro,
      requestData: state.requestData,
    }))
  );

  const {
    delsysCode,
    search,
    source,
    planName,
    policyNumber,
    planYear,
    pvrcode,
    clusterKey: clusterKeyFromDeeplink,
    stateCode: deeplinkStateCode,
  } = guestPlanSelectorData || {};

  useEffect(() => {
    if (selectedLocation) {
      setLocation(selectedLocation);
    }
  }, [selectedLocation]);
  useEffect(() => {
    if (location?.label) {
      setCurrentInputValue(location.label);
    }
  }, [location?.label]);
  const storedPlans = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS
  );
  const [availablePlans, setAvailablePlans] = useState<any[]>(
    storedPlans ? JSON.parse(storedPlans) : []
  );
  const stateCode = location?.stateCode;
  const { navigate, getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();
  const coverageType = searchParams?.get('coverageType') || '';
  const lob = searchParams?.get('planSelectionLob') || '';
  const chipValue = searchParams?.get('chipValue') || '';

  const isGuestDeeplink = source === 'deeplink';

  // Check if deeplink has plan identifier (delsysCode)
  const hasDeeplinkPlanIdentifier =
    isGuestDeeplink && (delsysCode || deeplinkStateCode);

  const flags = useFlags();
  const guestBGApiUrl = getBlueGreenGQLApiUrl(flags, searchParams);

  const lagoonMessagingTable = useLagoon('ui-messaging');
  const lagoonUIMessage =
    typeof lagoonMessagingTable === 'function'
      ? lagoonMessagingTable()
      : lagoonMessagingTable;

  const lagoonConfigTable = useLagoon('config');
  const planSelectionConfig =
    typeof lagoonConfigTable === 'function'
      ? lagoonConfigTable()
      : lagoonConfigTable;
  const acquisitionData = useLagoon('acquisition')?.();
  const matchedAcquisition = findAcquisitionByClientCode(
    acquisitionData,
    requestData?.client || ''
  );
  const isClientCodeMatched = Boolean(matchedAcquisition);
  const portalSourceFromLagoon = matchedAcquisition?.portalSource ?? 'psx';

  const signInUrl = planSelectionConfig?.find(
    (row: any) => row.key === `SIGNIN_URL_${lob?.toUpperCase()}`
  )?.value;

  const selectLocationMessageText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SELECT_LOCATION_MESSAGE,
  })?.message;
  const locationModalHeadingText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOCATION_MODAL_HEADING,
  })?.message;
  const locationModalSubheadingText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOCATION_MODAL_SUBHEADING,
  })?.message;
  const locationModalSignInText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOCATION_MODAL_SIGN_IN,
  })?.message;
  const locationModalSignInUrl = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOCATION_MODAL_SIGN_IN,
  })?.url;
  const locationModalSearchText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOCATION_MODAL_SEARCH,
  })?.message;
  const dualCompletePlanMessaging = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.DUAL_COMPLETE_PLAN_MESSAGING,
  })?.message;
  const selectLocationHeadingKey = getSelectLocationHeadingKey(
    planName,
    lob,
    coverageType
  );
  const selectLocationHeadingText = find(lagoonUIMessage, {
    key: selectLocationHeadingKey,
  })?.message;

  const showDualCompletePlanMessaging = getShowDualCompletePlanMessaging(
    coverageType,
    lob,
    selectedLocation
  );

  const [postalPointResult, getPostalPoint] = useGetPostalPoint({
    url: guestBGApiUrl,
  });

  const currentYear = String(
    DateTime.getCurrentDateTimeWithCentralOffset().getFullYear()
  );
  const [, getPlanDetails] = useGetPlanDefinitions({
    url: guestBGApiUrl,
    onCompleted: (result: {
      data: {
        getPlanDefinitions: {
          planCategories: [
            { planCategory: string; planDetails: [planDetails] }
          ];
          planDetails: [planDetails];
        };
      };
    }) => {
      const combinedPlans = getAvailablePlans(result?.data?.getPlanDefinitions);
      const filteredPlans = filterAvailablePlansByPlanName(
        combinedPlans,
        planName
      );

      setAvailablePlans(filteredPlans);
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS,
        JSON.stringify(filteredPlans)
      );
      setIsDataLoaded(true);
    },
  });

  const locationRequirement = getLocationRequirement(lob, coverageType);
  const [, getFipsCode] = useGetFipsCode({});
  const [, getDeeplinkLocation] = useLocation({
    url: guestBGApiUrl,
    onCompleted: (result: any) => {
      const locationResult = result?.data?.location?.features?.[0];
      if (!locationResult) return;
      const { center = [], place_name: placeName, stateCode } = locationResult;
      const [longitude, latitude] = center;
      setGeoLocationToStorage({
        name: placeName,
        longitude,
        latitude,
        stateCode,
      });
      const selectedLocation: GuestLocation = {
        label: placeName,
        stateCode,
        zipcode: '',
      };
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION,
        JSON.stringify(selectedLocation)
      );
      setLocation(selectedLocation);
      setSelectedLocation(selectedLocation);
    },
  });

  const isEI = lob === 'EI';
  const portalSource =
    guestPlanSelectorData?.portal ||
    (isClientCodeMatched ? portalSourceFromLagoon : 'psx');
  const isLawwGuestExperience = isLawwGuest();

  useEffect(() => {
    if (deeplinkStateCode) {
      getDeeplinkLocation({
        variables: { address: deeplinkStateCode },
      });
    }
  }, [deeplinkStateCode]);

  useEffect(() => {
    setIsDataLoaded(false);
    setAvailablePlans([]);
    sessionStorage.removeItem(Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS);
    sessionStorage.setItem('countyFipsCode', JSON.stringify(null));

    if (locationRequirement === 'n/a' && !isLawwGuestExperience) {
      getPlanDetails({
        variables: {
          planYear: planYear || currentYear,
          lob,
          coverageType,
          isDualYear: true,
          isEI,
          planIdentifier: delsysCode,
          portalSource,
          source,
        },
      });
    } else if (
      locationRequirement === 'county' &&
      psxLocation?.latitude &&
      psxLocation?.longitude
    ) {
      // Use countyId from postal API if available (for geospatial LOBs)
      if (location?.countyId && !isLawwGuestExperience) {
        const countyFipsCode = location.countyId;
        sessionStorage.setItem('countyFipsCode', countyFipsCode);
        getPlanDetails({
          variables: {
            planYear: planYear || currentYear,
            lob,
            coverageType,
            stateCode,
            countyFipsCode,
            isDualYear: true,
            isEI,
            planIdentifier: delsysCode,
            portalSource,
            source,
            ...(isClientCodeMatched && {
              clientCode: requestData?.client || '',
            }),
          },
        });
      } else {
        // Fall back to getFipsCode for other LOBs
        if (isNavigatingMaestro && !isLawwGuestExperience) {
          sessionStorage.setItem('countyFipsCode', requestData?.fipsCode);
          getPlanDetails({
            variables: {
              planYear: requestData?.year,
              lob,
              coverageType,
              stateCode,
              countyFipsCode: requestData?.fipsCode,
              isDualYear: true,
              isEI,
              planIdentifier: delsysCode,
              portalSource,
              source,
              ...(isClientCodeMatched && {
                clientCode: requestData?.client || '',
              }),
            },
          });
          return;
        }
        getFipsCode({
          variables: {
            lat: parseFloat(psxLocation?.latitude).toFixed(10),
            long: parseFloat(psxLocation?.longitude).toFixed(10),
          },
        }).then((res: any) => {
          const countyFipsCode = res?.data?.getFipsCode?.fipsCode;
          sessionStorage.setItem('countyFipsCode', countyFipsCode);
          if (!isLawwGuestExperience) {
            getPlanDetails({
              variables: {
                planYear: planYear || currentYear,
                lob,
                coverageType,
                stateCode,
                countyFipsCode,
                isDualYear: true,
                isEI,
                planIdentifier: delsysCode,
                portalSource,
                source,
              },
            });
          }
        });
      }
    } else if (location?.stateCode && !isLawwGuestExperience) {
      getPlanDetails({
        variables: {
          planYear: planYear || currentYear,
          lob,
          coverageType,
          stateCode,
          isDualYear: true,
          isEI,
          planIdentifier: delsysCode,
          portalSource,
          source,
        },
      });
    }
  }, [location?.stateCode, location?.countyId, lob, coverageType, planYear]);

  // ─── Navigation ──────────────────────────────────────────────────────────────

  /**
   * Single navigation function used by all selection flows (submit button,
   * modal search, geospatial confirmation, and deeplink auto-navigate).
   * Pass `fireAdobeEvent: true` for user-initiated searches.
   */
  const navigateAfterSelection = (
    loc: GuestLocation,
    { fireAdobeEvent = false } = {}
  ) => {
    const safeLocation = { ...loc };
    delete safeLocation.latitude;
    delete safeLocation.longitude;
    const plansAvailable: any[] = JSON.parse(
      sessionStorage.getItem(Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS) ||
        '[]'
    );
    const selectedPlan = plansAvailable[0]?.planDetails?.[0] || {};
    const stateCode = loc?.stateCode || '';
    if (fireAdobeEvent) {
      adobeStandardSearchEvent({
        isGuest: true,
        numberOfResults: plansAvailable.length,
        method: 'autofill',
        term: loc?.label || '',
        type: 'site',
      });
    }

    if (
      shouldSkipPlanSelection(plansAvailable) ||
      isLawwGuest() ||
      hasDeeplinkPlanIdentifier
    ) {
      if (!isLawwGuestExperience) {
        setGuestPlanSelectionData({
          selectedPlan,
          lob,
          coverageType,
          stateCode,
          planName,
          policyNumber,
          allConfigs: planSelectionConfig,
          pvrcode,
          clusterKeyFromDeeplink,
        });
      }
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION,
        JSON.stringify(safeLocation)
      );

      const guestPath = selectedPlan?.years?.find(
        (year) => year.planYear === String(currentYear)
      )?.guestPath;

      if (guestPath && !isGuestDeeplink) {
        window.open(guestPath, '_blank');
        return;
      } else if (isGuestDeeplink && search) {
        navigate(
          `/browse/results/?coverageType=${coverageType}&search=${encodeURIComponent(
            search
          )}&psx-source=deeplink`,
          {
            state: buildPortalData(
              selectedPlan,
              lob,
              coverageType || 'M',
              stateCode,
              undefined,
              signInUrl,
              undefined,
              planName,
              policyNumber,
              planSelectionConfig,
              pvrcode,
              clusterKeyFromDeeplink
            ),
          }
        );
      } else {
        navigate(
          `/browse?coverageType=${coverageType}&chipValue=${chipValue}`,
          {
            state: buildPortalData(
              selectedPlan,
              lob,
              coverageType || 'M',
              stateCode,
              undefined,
              signInUrl,
              undefined,
              planName,
              policyNumber,
              planSelectionConfig,
              pvrcode,
              clusterKeyFromDeeplink
            ),
          }
        );
      }
    } else {
      const planYears = availablePlans[0]?.planDetails?.[0]?.years?.length || 0;
      const shouldShowYearSelection =
        availablePlans?.length === 1 && planYears > 1;

      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION,
        JSON.stringify(safeLocation)
      );

      if (shouldShowYearSelection) {
        setGuestPlanSelectionData({
          selectedPlan,
          coverageType,
          lob,
          stateCode,
          planName,
          policyNumber,
          allConfigs: planSelectionConfig,
          pvrcode,
          clusterKeyFromDeeplink,
        });
      }

      const path = shouldShowYearSelection
        ? 'plan-year-selection'
        : 'plan-selection';

      navigate(
        `/${path}?planSelectionLob=${lob}&coverageType=${coverageType}&chipValue=${chipValue}`,
        {
          state: availablePlans,
          replace: true,
        }
      );
    }
  };

  // ─── Location change handlers ─────────────────────────────────────────────────

  /** Resets multicounty state and updates the active location. */
  const handleDirectLocationSet = (loc: GuestLocation) => {
    setShowCountyDropdown(false);
    setSelectedCounty('');
    setPendingLocation(undefined);
    setUsPostalPointId('');
    sessionStorage.removeItem(
      Constants.STORAGE_KEYS.SESSION.IS_COUNTY_SELECTED
    );
    setLocation(loc);
  };

  /**
   * Called when the user picks a location from the autocomplete.
   * Queries the postal-point API; if the ZIP spans multiple counties the UI
   * branches based on context: modal → inline county dropdown,
   * main page → GeospatialModal.
   */
  const handlePostalLocationSelected = async (
    data: PostalLocationData,
    isModal: boolean
  ) => {
    const { selectedLocation: pickedLocation, variables } = data;

    // Reset county state immediately while the API call is in flight.
    setShowCountyDropdown(false);
    setSelectedCounty('');
    setPendingLocation(undefined);
    setUsPostalPointId('');

    try {
      const postalPointResponse = await getPostalPoint({ variables });
      const postalPointData = postalPointResponse?.data?.getPostalPoint;
      const postalPoint = Array.isArray(postalPointData)
        ? postalPointData?.[0]
        : postalPointData;

      const multicountyFlag = String(
        postalPoint?.multicounty_flag || ''
      ).trim();

      if (multicountyFlag === 'Y' && postalPoint?.us_postal_point_id) {
        // Multi-county ZIP: let the user choose their county.
        setPendingLocation({
          ...pickedLocation,
          stateCode: postalPoint?.state || pickedLocation.stateCode,
          countyId: postalPoint?.county_id || undefined,
        });
        setUsPostalPointId(postalPoint.us_postal_point_id);

        if (isModal) {
          setShowCountyDropdown(true);
        } else {
          setIsGeospatialOpen(true);
        }
        return;
      }

      sessionStorage.removeItem(
        Constants.STORAGE_KEYS.SESSION.IS_COUNTY_SELECTED
      );
      setLocation({
        ...pickedLocation,
        stateCode: postalPoint?.state || pickedLocation.stateCode,
        countyId: postalPoint?.county_id || undefined,
      });
    } catch {
      setLocation(pickedLocation);
      sessionStorage.removeItem(
        Constants.STORAGE_KEYS.SESSION.IS_COUNTY_SELECTED
      );
    }
  };

  /**
   * Shared county-confirmation logic used by both the GeospatialModal (main
   * page) and the inline county dropdown inside the edit-location modal.
   */
  const handleConfirmCountySelection = () => {
    if (!selectedCounty || !pendingLocation) return;

    const [countyStateCode, countyId] = selectedCounty.split('-');
    const confirmedLocation = {
      ...pendingLocation,
      stateCode: countyStateCode,
      countyId,
    };

    setLocation(confirmedLocation);
    setSelectedLocation(confirmedLocation);
    setIsGeospatialOpen(false);
    setShowCountyDropdown(false);
    setRequireNavigateAfterLocationSelect(true);
    sessionStorage.setItem('countyFipsCode', countyId);
    sessionStorage.setItem(
      Constants.STORAGE_KEYS.SESSION.IS_COUNTY_SELECTED,
      'true'
    );
  };

  /**
   * Handles the edit-location modal search button.
   * When the inline county dropdown is visible the user must confirm their
   * county first; otherwise perform the standard location search.
   */
  const handleModalSearch = () => {
    if (showCountyDropdown) {
      handleConfirmCountySelection();
      setIsOpen(false);
      return;
    }
    adobeLinkTrackEvent({
      name: locationModalSearchText,
      location: locationModalAdobeLabel,
      type: 'internal',
      destinationUrl: '',
    });
    if (location) {
      adobeStandardSearchEvent({
        isGuest: true,
        numberOfResults: availablePlans.length,
        method: 'autofill',
        term: location?.label || '',
        type: 'site',
      });
      setSelectedLocation(location);
    }
    setIsOpen(false);
  };

  // ─── Deep-link auto-navigation ────────────────────────────────────────────────
  useEffect(() => {
    const guestLocation = sessionStorage.getItem(
      Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION
    );
    if (
      (hasDeeplinkPlanIdentifier || requireNavigateAfterLocationSelect) &&
      isDataLoaded &&
      availablePlans.length > 0 &&
      guestLocation
    ) {
      setRequireNavigateAfterLocationSelect(false);
      navigateAfterSelection(location);
    }
  }, [
    hasDeeplinkPlanIdentifier,
    isDataLoaded,
    availablePlans,
    location,
    requireNavigateAfterLocationSelect,
  ]);

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    guestLob: getLobForPortalData(lob),
    isGuest: true,
    pageName: getGuestLocationPageName(coverageType),
    sitesectionLevel1: 'guest plan selection',
    sitesectionLevel2: 'select coverage type',
  });

  useEffect(() => {
    adobePageTrackEvent();
    // Navigate to browse for deeplinks with single plan OR with plan identifier (delsysCode)
    const guestLocation = sessionStorage.getItem(
      Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION
    );
    if (isGuestDeeplink && hasDeeplinkPlanIdentifier && guestLocation) {
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS,
        JSON.stringify(availablePlans)
      );
      navigateAfterSelection(JSON.parse(guestLocation));
    }
  }, []);

  const locationModalAdobeLabel = 'edit location modal';
  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new Event('modalOpened'));
      adobeModalTrackEvent(locationModalAdobeLabel);
    }
  }, [isOpen]);

  return (
    <React.Fragment>
      {!matchedAcquisition?.hideSignInHeader && (
        <GeospatialModal
          isOpen={isGeospatialOpen}
          lob={lob}
          onClose={() => setIsGeospatialOpen(false)}
          onSearch={handleConfirmCountySelection}
          selectedCounty={selectedCounty}
          setSelectedCounty={setSelectedCounty}
          usPostalPointId={usPostalPointId}
        />
      )}
      <LocationModal
        footer={
          <Layout.Group alignLayout="right" style={{ gap: 8 }}>
            <V2Button
              data-auto-testid="modal-search-button"
              data-testid="modal-search-button"
              isDisabled={
                selectedLocation === undefined ||
                !isDataLoaded ||
                !currentInputValue?.trim() ||
                postalPointResult?.loading ||
                (showCountyDropdown && !selectedCounty?.trim())
              }
              onClick={handleModalSearch}
              size="$md"
              type="button"
            >
              {locationModalSearchText}
            </V2Button>
            <V2Button
              css={{ marginLeft: '8px' }}
              data-auto-testid="modal-sign-in-button"
              data-testid="modal-sign-in-button"
              href={locationModalSignInUrl}
              onClick={() => {
                adobeLinkTrackEvent({
                  name: locationModalSignInText,
                  location: locationModalAdobeLabel,
                  type: 'internal',
                  destinationUrl: locationModalSignInUrl,
                });
              }}
              openNewWindow={false}
              size="$md"
              variant="outline"
            >
              {locationModalSignInText}
            </V2Button>
          </Layout.Group>
        }
        header={locationModalHeadingText}
        isOpen={isOpen}
        onClose={() => {
          adobeLinkTrackEvent({
            name: 'close',
            location: locationModalAdobeLabel,
            type: 'internal',
            destinationUrl: '',
          });
          setIsOpen(false);
        }}
      >
        <div>
          <Text
            css={{ marginBottom: '16px', display: 'block' }}
            data-auto-testid="modal-location-heading"
            data-testid="modal-location-heading"
          >
            {locationModalSubheadingText}
          </Text>
          <GuestLocationSearch
            coverageType={coverageType}
            currentInputValue={currentInputValue}
            isCountyDropdownLoading={postalPointResult?.loading}
            isDataLoaded={isDataLoaded}
            isLocationModal={true}
            lob={lob}
            location={location}
            onPostalLocationSelected={(data) =>
              handlePostalLocationSelected(data, true)
            }
            selectedCounty={selectedCounty}
            setCurrentInputValue={setCurrentInputValue}
            setLocation={handleDirectLocationSet}
            setSelectedCounty={setSelectedCounty}
            setShowCountyDropdown={setShowCountyDropdown}
            showCountyDropdown={showCountyDropdown}
            usPostalPointId={usPostalPointId}
          />
        </div>
      </LocationModal>

      <Flex
        alignContent="center"
        css={{ marginBottom: '20px', gap: '8px' }}
        direction="column"
        justify="center"
      >
        <Heading offset={3} textAlign="center">
          {selectLocationHeadingText}
        </Heading>
        <MessagingText size="$md">
          {!planName && selectLocationMessageText}
        </MessagingText>
        {showDualCompletePlanMessaging && (
          <MessagingText
            data-auto-testid="dual-complete-plan-messaging"
            data-testid="dual-complete-plan-messaging"
            size="$md"
          >
            {dualCompletePlanMessaging}
          </MessagingText>
        )}
      </Flex>

      {selectedLocation ? (
        <SelectedLocationWrapper
          alignItems="center"
          direction="row"
          id="selected-location-container"
          justify="center"
        >
          <IconSymbol color="$gray8" icon="location_on" />
          <Text>{selectedLocation?.label}</Text>
          <Button
            aria-haspopup="dialog"
            data-testid="edit-location-button"
            onClick={() => {
              adobeLinkTrackEvent({
                name: t('PRIMARY_CARE_PROVIDER.EDIT'),
                location: 'Choose a plan',
                type: 'internal',
                destinationUrl: '',
              });
              setIsOpen(true);
            }}
            size={'$sm'}
            variant="tertiary"
          >
            {t('PRIMARY_CARE_PROVIDER.EDIT')}
          </Button>
        </SelectedLocationWrapper>
      ) : (
        <LocationSearchBoxWrapper justify="center">
          <GuestLocationSearch
            coverageType={coverageType}
            currentInputValue={currentInputValue}
            isCountyDropdownLoading={postalPointResult?.loading}
            isDataLoaded={isDataLoaded}
            lob={lob}
            location={location}
            onPostalLocationSelected={(data) =>
              handlePostalLocationSelected(data, false)
            }
            onSubmit={(loc) => {
              const lc = loc || location;
              if (lc) setSelectedLocation(lc);
              navigateAfterSelection(lc as GuestLocation, {
                fireAdobeEvent: true,
              });
            }}
            selectedCounty={selectedCounty}
            setCurrentInputValue={setCurrentInputValue}
            setLocation={handleDirectLocationSet}
            setSelectedCounty={setSelectedCounty}
            setShowCountyDropdown={setShowCountyDropdown}
            showCountyDropdown={showCountyDropdown}
            usPostalPointId={usPostalPointId}
          />
        </LocationSearchBoxWrapper>
      )}
    </React.Fragment>
  );
};
